import React, { useEffect, useState } from "react";
import MetaTags from "react-meta-tags";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import axios from "../api/axios";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { monogramInitials } from "./vendorMonogram";

const VENDOR_PROFILE = "/vendor/profile";
const GET_VENDOR_LOGO_URL = "/vendor/logo-url";
const REQUEST_VENDOR_LOGO_UPLOAD = "/vendor/logo/request-upload";
const VENDOR_LOGO = "/vendor/logo";

// One consistent tile shape for every logo state (uploaded image, generated
// monogram, or empty) so the layout never jumps as a vendor uploads/removes.
const LOGO_TILE_STYLE = {
  width: "64px",
  height: "64px",
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  background: "#fff",
  border: "1px solid #e9e9ef",
  borderRadius: "6px",
};

const VendorProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [agencyName, setAgencyName] = useState("");

  const [logoUrl, setLogoUrl] = useState(null);
  const [logoFilename, setLogoFilename] = useState("");
  const [logoBusy, setLogoBusy] = useState(false);

  const loadLogo = async () => {
    try {
      const { data } = await axios.get(GET_VENDOR_LOGO_URL);
      setLogoUrl(data?.url || null);
      setLogoFilename(data?.filename || "");
    } catch (error) {
      setLogoUrl(null);
      setLogoFilename("");
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(VENDOR_PROFILE);
        if (cancelled) return;
        const p = data?.profile || {};
        setCompanyName(p.company_name || "");
        setWebsite(p.company_website || "");
        setGstNumber(p.gst_number || "");
        setAgencyName(data?.agency_name || "");
      } catch (error) {
        if (!cancelled) {
          toast.error(
            error?.response?.data?.message || "Could not load your company profile"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    loadLogo();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(VENDOR_PROFILE, {
        company_name: companyName,
        company_website: website,
        gst_number: gstNumber,
      });
      toast.success("Company profile saved");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Could not save the company profile"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be 2 MB or smaller");
      return;
    }
    setLogoBusy(true);
    try {
      const { data } = await axios.post(REQUEST_VENDOR_LOGO_UPLOAD, {
        fileName: file.name,
        contentType: file.type,
        size: file.size,
      });
      await fetch(data.signedUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      await axios.put(VENDOR_LOGO, {
        key: data.key,
        filename: file.name,
        mime: file.type,
      });
      await loadLogo();
      toast.success("Logo saved");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not upload the logo");
    } finally {
      setLogoBusy(false);
    }
  };

  const handleRemoveLogo = async () => {
    setLogoBusy(true);
    try {
      await axios.delete(VENDOR_LOGO);
      await loadLogo();
      toast.success("Logo removed");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not remove the logo");
    } finally {
      setLogoBusy(false);
    }
  };

  // What the invoice header will actually carry when no logo is uploaded.
  const brandName = (companyName || agencyName || "").trim();
  const monogram = monogramInitials(brandName);

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Company Profile | Rentdigicare</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title="Vendor" breadcrumbItem="Company Profile" />
          <Row>
            <Col lg={8} xl={7}>
              <Card style={{ border: "1px solid #e9e9ef" }}>
                <CardBody className="p-4">
                  <h4 className="card-title mb-1">Company Profile</h4>
                  <p className="text-muted mb-4">
                    These details appear on the invoices you submit. Every field
                    is optional.
                  </p>

                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner size="sm" color="primary" />
                    </div>
                  ) : (
                    <React.Fragment>
                      <div>
                        <Label className="form-label mb-2">
                          Company logo{" "}
                          <span className="text-muted fw-normal">
                            (optional — appears on invoice PDFs)
                          </span>
                        </Label>
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                          <div
                            aria-hidden={!logoUrl && !monogram}
                            style={LOGO_TILE_STYLE}
                          >
                            {logoUrl ? (
                              <img
                                src={logoUrl}
                                alt={logoFilename || "Company logo"}
                                style={{
                                  maxWidth: "56px",
                                  maxHeight: "56px",
                                  objectFit: "contain",
                                }}
                              />
                            ) : monogram ? (
                              // Colors pinned to the invoice PDF's own navy/gold
                              // masthead (Server/.../invoice.pdf.service.ts), not
                              // the app's brand blue — this is a live preview of
                              // exactly what prints, so it must match the PDF.
                              <span
                                style={{
                                  color: "#fff",
                                  background: "#16253F",
                                  borderBottom: "3px solid #B8862B",
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "18px",
                                  fontWeight: 600,
                                  letterSpacing: "1.5px",
                                }}
                              >
                                {monogram}
                              </span>
                            ) : (
                              <span
                                className="text-muted"
                                style={{ fontSize: "11px", textAlign: "center" }}
                              >
                                No logo
                              </span>
                            )}
                          </div>

                          <div className="flex-grow-1" style={{ minWidth: "180px" }}>
                            <div className="fw-semibold">
                              {brandName ||
                                (logoUrl ? "Company logo" : "Unnamed company")}
                            </div>
                            <div className="text-muted" style={{ fontSize: "12px" }}>
                              {logoUrl
                                ? logoFilename || "Custom logo uploaded"
                                : monogram
                                ? "Generated monogram — shown until you upload a logo"
                                : "Add a company name to preview your monogram"}
                            </div>
                          </div>

                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              disabled={logoBusy}
                              onClick={() =>
                                document
                                  .getElementById("vendorProfileLogoInput")
                                  .click()
                              }
                            >
                              {logoBusy ? (
                                <Spinner size="sm" />
                              ) : logoUrl ? (
                                "Replace"
                              ) : (
                                "Upload"
                              )}
                            </button>
                            {logoUrl && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                disabled={logoBusy}
                                onClick={handleRemoveLogo}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <input
                            id="vendorProfileLogoInput"
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            style={{ display: "none" }}
                            onChange={handleLogoFileChange}
                          />
                        </div>
                      </div>

                      <div
                        className="mt-4 pt-4"
                        style={{ borderTop: "1px solid #e9e9ef" }}
                      >
                        <div className="mb-3">
                          <Label className="form-label">Company Name</Label>
                          <Input
                            type="text"
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            placeholder="e.g. Four Corner Services"
                          />
                          {!companyName && agencyName && (
                            <div className="form-text">
                              Left blank, invoices use your agency name &ldquo;
                              {agencyName}&rdquo;.
                            </div>
                          )}
                        </div>

                        <Row>
                          <Col md={6}>
                            <div className="mb-3 mb-md-0">
                              <Label className="form-label">Company Website</Label>
                              <Input
                                type="text"
                                value={website}
                                onChange={(e) => setWebsite(e.target.value)}
                                placeholder="e.g. fourcorner.ca"
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div>
                              <Label className="form-label">GST No.</Label>
                              <Input
                                type="text"
                                value={gstNumber}
                                onChange={(e) => setGstNumber(e.target.value)}
                                placeholder="e.g. 123456789 RT0001"
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>

                      <div
                        className="mt-4 pt-3 d-flex"
                        style={{ borderTop: "1px solid #e9e9ef" }}
                      >
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={saving}
                          onClick={handleSave}
                        >
                          {saving ? (
                            <React.Fragment>
                              <Spinner size="sm" className="me-2" />
                              Saving…
                            </React.Fragment>
                          ) : (
                            "Save Profile"
                          )}
                        </button>
                      </div>
                    </React.Fragment>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default VendorProfile;
